import { Box, Button, Checkbox, Divider, Paper, Stack, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import PageContainer from '../../../components/common/PageContainer';
import AppTextField from '../../../components/common/forms/AppTextField';
import { appColors, appStyles } from '../../../theme';
import { MODULES, PermissionTypes, RoleFields, roleInitialValues, roleSchema, EquipmentRepairProcessModule, EquipmentRentalProcessModule } from './roles-utils';
import { useSnackBar } from '../../../components/ToastProvider';
import { useCreateRole, useGetRoleById, useUpdateRole } from '../../../hooks/useRoles';
import { useEffect } from 'react';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import { usePermission } from "../../../hooks/usePermissions";
import { Modules, Permissions } from "../Roles/roles-utils";

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const hasAddPermission = usePermission(
    Modules.role.accessKey,
    Permissions.add
  );
  const hasEditPermission = usePermission(
    Modules.role.accessKey,
    Permissions.edit
  );
  const { showSnackBar } = useSnackBar();
  const currentRole = useGetRoleById();
  const updateRole = useUpdateRole();
  const createRole = useCreateRole();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RoleFields>({
    defaultValues: roleInitialValues,
    mode: 'onChange',
    resolver: yupResolver(roleSchema),
  });

  const permissionValues = watch('permissions');

  useEffect(() => {
    if (!currentRole.data) return;

    Object.keys(currentRole.data).forEach((key) => {
      const _key = key as keyof RoleFields;
      setValue(_key, currentRole.data[_key]);
    });
  }, [currentRole.data, setValue]);

  if (!hasAddPermission && !hasEditPermission) return <Navigate to="/home" />;
  
  const onError = (err: typeof errors) => {
    console.log(err);
  };

  const onSubmit: SubmitHandler<RoleFields> = (role) => {
    if (currentRole.data) {
      updateRole.mutate(role);
    }
    if (!currentRole.data) {
      createRole.mutate(role);
    }
  };

  const backTo = () => navigate('/roles');

  if (id !== undefined) {
    if (currentRole.isError) {
      return <h1>Error</h1>;
    }
    if (updateRole.isLoading || currentRole.isLoading) {
      return <LoadingIndicator show />;
    }
  }
  if (createRole.isLoading) {
    return <LoadingIndicator show />;
  }

  return (
    <>
      <PageContainer
        title='User Management'
        subTitle='Roles & Permission'
        controls={
          <Stack direction='row' gap={2}>
            <Button sx={{ backgroundColor: appColors.lightGray, color: 'black' }} onClick={backTo}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit, onError)}
              disableElevation
              variant='contained'
              sx={{ textTransform: 'capitalize', borderRadius: 1.5 }}
            >
              Save
            </Button>
          </Stack>
        }
      >
        <Divider color='black' />
        <Stack
          component='form'
          height='100%'
          bgcolor='white'
          boxShadow={appStyles.shadow}
          borderRadius={1}
        >
          <Stack px={3} height='100%' overflow='auto' gap={3}>
            <Stack
              direction='row'
              gap={3}
              width={500}
              justifyContent='center'
              margin='auto'
              mt={3}
              mb={0}
            >
              <AppTextField
                required
                label='Role'
                placeholder='Role'
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
              <AppTextField
                required
                label='Role Description'
                placeholder='Role description'
                multiline
                rows={1}
                {...register('description')}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </Stack>
            {/* HERE */}
            <Paper elevation={1}>
              <Stack fontSize={13}>
                {/* TITLE HEADER */}
                <Box
                  display='grid'
                  gridTemplateColumns='240px repeat(12, 1fr)'
                  justifyContent='space-between'
                  bgcolor='primary.main'
                  color='white'
                  border='0'
                  borderRadius='8px 8px 0 0'
                  padding={1}
                  paddingLeft={2}
                >
                  <Typography fontSize='inherit' textTransform='capitalize' fontWeight='600'>
                    Modules
                  </Typography>
                  <Typography
                    fontSize='inherit'
                    textTransform='capitalize'
                    textAlign={'left'}
                    fontWeight='600'
                  >
                    All
                  </Typography>
                  {PermissionTypes.map((p) => (
                    <Typography
                      key={p}
                      fontSize='inherit'
                      textTransform='capitalize'
                      textAlign={'left'}
                      fontWeight='600'
                    >
                      {p.split('_').join(' ').toLowerCase()}
                    </Typography>
                  ))}
                </Box>
                <Stack overflow='auto' style={{ maxHeight: 'calc(100vh - 350px)', minHeight: '300px' }}>
                  {MODULES.map((m, index) => (
                    <>
                      <Accordion defaultExpanded sx={{
                        boxShadow: 'none',
                        border: 'none',
                        '&:before': {
                          display: 'none',
                        },
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                        borderBottom: '1px solid #aaa',
                      }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel-content${index}`}
                          id={`panel-header${index}`}
                        >
                        <Stack direction='row'>
                          {m.icon}
                          <Typography fontSize='inherit' fontWeight='600'>
                            {m.title}
                          </Typography>
                        </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          {m.modules.map((md) => (
                            <>
                              <Box
                                key={md.accessKey}
                                display='grid'
                                gridTemplateColumns='240px repeat(12, 1fr)'
                                alignItems='center'
                              >
                                <Typography fontSize='inherit' fontWeight='600' ml={10}>
                                  {md.label}
                                </Typography>
                                <Checkbox
                                  sx={{ mr: 'auto' }}
                                  checked={
                                    PermissionTypes
                                      .filter(p =>
                                        ('permissions' in md && Array.isArray(md.permissions) && md.permissions.includes(p))
                                      )
                                      .every(p =>
                                        permissionValues.includes([md.accessKey.toUpperCase(), p].join('::'))
                                      )
                                  }
                                  indeterminate={
                                    PermissionTypes
                                      .filter(p =>
                                        ('permissions' in md && Array.isArray(md.permissions) && md.permissions.includes(p))
                                      )
                                      .some(p =>
                                        permissionValues.includes([md.accessKey.toUpperCase(), p].join('::'))
                                      )
                                    &&
                                    !PermissionTypes
                                      .filter(p =>
                                        ('permissions' in md && Array.isArray(md.permissions) && md.permissions.includes(p))
                                      )
                                      .every(p =>
                                        permissionValues.includes([md.accessKey.toUpperCase(), p].join('::'))
                                      )
                                  }
                                  onChange={(e, checked) => {
                                    let newPermissions = [...permissionValues];
                                    PermissionTypes.forEach(p => {
                                      const isEnabled =
                                        ('permissions' in md && Array.isArray(md.permissions) && md.permissions.includes(p));
                                      const permKey = [md.accessKey.toUpperCase(), p].join('::');

                                      if (isEnabled) {
                                        if (checked) {
                                          if (!newPermissions.includes(permKey)) {
                                            newPermissions.push(permKey);
                                          }
                                        } else {
                                          newPermissions = newPermissions.filter(val => val !== permKey);
                                        }
                                      }
                                    });
                                    setValue('permissions', newPermissions);
                                  }}
                                  />
                                {PermissionTypes.map((p) =>
                                  (
                                    ('permissions' in m && Array.isArray(m.permissions) && m.permissions.includes(p)) ||
                                    ('permissions' in md && Array.isArray(md.permissions) && md.permissions.includes(p))
                                  ) ? (
                                    <Checkbox
                                      key={p}
                                      sx={{ mr: 'auto' }}
                                      onChange={(e, checked) => {
                                        const currentPermission = [md.accessKey.toUpperCase(), p].join('::');

                                        const permissionArray = checked
                                          ? [...permissionValues, currentPermission]
                                          : permissionValues!.filter((pv) => pv !== currentPermission);

                                        if (p === 'VIEW' && !checked) {
                                          const newPermissionArray = permissionArray.filter(
                                            (pv) => !pv.startsWith(md.accessKey.toUpperCase())
                                          );
                                          setValue('permissions', newPermissionArray);
                                          return;
                                        }

                                        if (p !== 'VIEW' && checked) {
                                          const currentPermission = [md.accessKey.toUpperCase(), 'VIEW'].join('::');

                                          const exists = permissionArray.find(
                                            (pv) => pv === currentPermission
                                          );

                                          if (!exists) {
                                            const newPermissionArray = [...permissionArray, currentPermission];
                                            setValue('permissions', newPermissionArray);
                                            return;
                                          }
                                        }

                                        setValue('permissions', permissionArray);
                                      }}
                                      checked={permissionValues.includes(
                                        [md.accessKey.toUpperCase(), p].join('::')
                                      )}
                                    />
                                  ) : (
                                  <Checkbox
                                      key={p}
                                      sx={{ mr: 'auto', opacity: 0.5 }}
                                      disabled
                                    />
                                  )
                                )}
                              </Box>
                            </>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </>
                  ))}
                  <Stack>
                    <Paper elevation={1}>
                      <Stack fontSize={13}>
                        {/* TITLE HEADER */}
                        <Box
                          display='grid'
                          gridTemplateColumns='240px repeat(6, 1fr)'
                          justifyContent='space-between'
                          bgcolor='primary.main'
                          color='white'
                          p={1}
                          border='0'
                        >
                          <Typography fontSize='inherit' textTransform='capitalize' fontWeight='600'>
                            {}
                          </Typography>
                          {EquipmentRepairProcessModule.permissions.map((p) => (
                            <Typography
                              key={p.accessKey}
                              fontSize='inherit'
                              fontWeight='600'
                              textAlign={'left'}
                            >
                              {p.label}
                            </Typography>
                          ))}
                        </Box>
                        <Box
                          display='grid'
                          gridTemplateColumns='245px repeat(6, 1fr)'
                          alignItems='center'
                          p={0.5}
                          paddingLeft={1.2}
                        >
                          <Stack direction='row' gap={1} p={1}>
                            {EquipmentRepairProcessModule.icon}
                            <Typography fontSize='inherit' fontWeight='600'>
                              {EquipmentRepairProcessModule.title}
                            </Typography>
                          </Stack>
                          {EquipmentRepairProcessModule.permissions.map((p) => (
                            <Checkbox
                              key={p.accessKey}
                              sx={{ mr: 'auto' }}
                              onChange={(e, checked) => {
                                const currentPermission = p.accessKey;

                                const permissionArrayTemp = permissionValues!.filter((pv) => !EquipmentRepairProcessModule.permissions.map(a => a.accessKey).includes(pv));
                                const permissionArray = checked
                                  ? [...permissionArrayTemp, currentPermission]
                                  : permissionArrayTemp;
                                setValue('permissions', permissionArray);
                              }}
                              checked={permissionValues.includes(
                                p.accessKey
                              )}
                            />
                          ))}
                        </Box>
                      </Stack>
                    </Paper>
                    <Paper elevation={1}>
                      <Stack fontSize={13}>
                        {/* TITLE HEADER */}
                        <Box
                          display='grid'
                          gridTemplateColumns='240px repeat(6, 1fr)'
                          justifyContent='space-between'
                          bgcolor='primary.main'
                          color='white'
                          p={1}
                          border='0'
                        >
                          <Typography fontSize='inherit' textTransform='capitalize' fontWeight='600'>
                            {}
                          </Typography>
                          {EquipmentRentalProcessModule.permissions.map((p) => (
                            <Typography
                              key={p.accessKey}
                              fontSize='inherit'
                              fontWeight='600'
                            >
                              {p.label}
                            </Typography>
                          ))}
                        </Box>
                        <Box
                          display='grid'
                          gridTemplateColumns='245px repeat(6, 1fr)'
                          alignItems='center'
                          p={0.5}
                          paddingLeft={1.2}
                        >
                          <Stack direction='row' gap={1} p={1}>
                            {EquipmentRentalProcessModule.icon}
                            <Typography fontSize='inherit' fontWeight='600'>
                              {EquipmentRentalProcessModule.title}
                            </Typography>
                          </Stack>
                          {EquipmentRentalProcessModule.permissions.map((p) => (
                            <Checkbox
                              key={p.accessKey}
                              sx={{ mr: 'auto' }}
                              onChange={(e, checked) => {
                                const currentPermission = p.accessKey;

                                const permissionArrayTemp = permissionValues!.filter((pv) => !EquipmentRentalProcessModule.permissions.map(a => a.accessKey).includes(pv));
                                const permissionArray = checked
                                  ? [...permissionArrayTemp, currentPermission]
                                  : permissionArrayTemp;
                                setValue('permissions', permissionArray);
                              }}
                              checked={permissionValues.includes(
                                p.accessKey
                              )}
                            />
                          ))}
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </PageContainer>
      {id !== undefined
        ? (updateRole?.isError || createRole?.isError) && showSnackBar('Network Error', 'error')
        : createRole?.isError && showSnackBar('Network Error', 'error')}
    </>
  );
};

export default RoleForm;
